import React from 'react';
import PropTypes from 'prop-types';
import Card from 'reactstrap/lib/Card';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { InventoryEntities } from 'client/data/models/inventory';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { SOURCES } from 'client/utils/image-helpers';
import { buildVinPreviewSrpLink } from 'site-modules/shared/utils/srp-link-constructor';
import { getVehicleName } from 'site-modules/shared/utils/inventory/usurp-inventory-card';
import { getUsurpImages } from 'site-modules/shared/utils/inventory/srp-images';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { isDeliveryEligible, isDriveway, isInstockOnline } from 'site-modules/shared/utils/dealer-details-utils';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { Link } from 'site-modules/shared/components/link/link';
import { Image } from 'site-modules/shared/components/image/image';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { ContactDealerSrp } from 'site-modules/shared/components/inventory/contact-dealer/contact-dealer-srp';

import './mini-inventory-card.scss';

const IN_STOCK_ONLINE = 'In-stock online';

export function MiniInventoryCard({
  initialFilter,
  inventoryVehicle,
  radius = null,
  zipCode = null,
  isInternational = false,
  hasVehicleParams = false,
  hasLeadForm = false,
  isMobile = false,
}) {
  const { vin, type, dealerInfo } = inventoryVehicle;
  const isNewType = isNew(type);

  const vdpPreviewSrpLink = buildVinPreviewSrpLink(vin, initialFilter);

  const { photoUrls } = getUsurpImages(inventoryVehicle);
  const photoUrl = photoUrls?.[0];

  const [firstLine, secondLine] = getVehicleName(inventoryVehicle, true, true);
  const vehicleName = hasVehicleParams ? secondLine || firstLine : firstLine;

  const displayPrice = get(inventoryVehicle, 'prices.advertisedPrice');

  const parentDealershipName = get(inventoryVehicle, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
  const isDrivewayDealer = isDriveway(parentDealershipName);
  const isDeliveryAvailable = isDeliveryEligible(inventoryVehicle, zipCode);
  const distance = Math.round(get(dealerInfo, 'distance', 0));

  const isInStockOnline =
    (!isNewType || isDrivewayDealer) && isInstockOnline({ distance, radius, isDeliveryAvailable });

  return (
    <Card className="mini-inventory-card rounded-12 bg-white overflow-hidden h-100">
      <div className={classnames('pos-r flex-fill', { 'pb-0_5': !hasLeadForm, 'mb-1_5': hasLeadForm })}>
        <AspectRatioContainer className="mb-0_5" aspectRatio="3:2">
          <Image imageId={photoUrl} source={SOURCES.EDMUNDSCDNASSETS} lazy />
        </AspectRatioContainer>
        <div className="d-flex flex-column justify-content-between px-0_75">
          <Link
            to={vdpPreviewSrpLink}
            className={classnames('link fw-bold text-primary-darker mb-0_75', { 'text-underline': hasLeadForm })}
            data-tracking-id="view_vin_preview"
          >
            {vehicleName}
          </Link>
          <div>
            <div className="small mb-0_25">
              {isNewType && 'Dealer price: '}
              <span className="size-24 fw-bold">{getPriceString(displayPrice)}</span>
            </div>
            {!isInternational && (
              <div className="medium text-cool-gray-40">
                <span className="icon-location small me-0_25" role="img" aria-label="Distance to dealer" />
                {isInStockOnline ? IN_STOCK_ONLINE : `${numberWithCommas(distance)} mi away`}
              </div>
            )}
          </div>
        </div>
      </div>
      {hasLeadForm && (
        <ContactDealerSrp
          vinData={inventoryVehicle}
          rootClassName="g-0 px-0_75 pb-1"
          radius={radius}
          isNational={isInternational}
          isMobile={isMobile}
          buttonColor="text-capitalize fw-medium rounded-8 medium px-0_75 py-0_25"
          ariaLabel={`${firstLine} ${secondLine}`}
          photoUrl={photoUrls[0]}
          isInStockOnline={isInStockOnline}
        />
      )}
    </Card>
  );
}

MiniInventoryCard.propTypes = {
  inventoryVehicle: InventoryEntities.InventoryVin.isRequired,
  initialFilter: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]))
    .isRequired,
  radius: PropTypes.number,
  zipCode: PropTypes.string,
  isInternational: PropTypes.bool,
  isMobile: PropTypes.bool,
  hasVehicleParams: PropTypes.bool,
  hasLeadForm: PropTypes.bool,
};
